import { useState, useEffect, useCallback, useRef } from 'react';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { ThemeName } from '@/contexts/ThemeContext';
import { themes as registryThemes } from '@/lib/theme-registry';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Palette, Monitor, Moon, Sun, Check } from 'lucide-react';

const themes: Array<{
  name: ThemeName;
  label: string;
  description: string;
}> = registryThemes.map((t) => ({
  name: t.name as ThemeName,
  label: t.displayName,
  description: t.description,
}));

const modes: Array<{
  value: string;
  label: string;
  description: string;
  icon: React.ReactNode;
}> = [
  { value: 'light', label: 'Light', description: 'Always light mode', icon: <Sun className="h-4 w-4 mr-2" /> },
  { value: 'dark', label: 'Dark', description: 'Always dark mode', icon: <Moon className="h-4 w-4 mr-2" /> },
  { value: 'system', label: 'System', description: 'Follow system preference', icon: <Monitor className="h-4 w-4 mr-2" /> },
];

function createDelayedSetter(setter: (v: any) => void, setSheetOpen: (v: boolean) => void, isMobile: boolean) {
  return (value: any) => {
    if (isMobile) {
      setSheetOpen(false);
      setTimeout(() => setter(value), 350);
    } else {
      setter(value);
    }
  };
}

function SystemModeLabel({ mode, systemTheme }: { mode: string; systemTheme: string | undefined }) {
  if (mode !== 'system' || !systemTheme) return null;
  return (
    <div className="mt-2 px-4 py-2 text-xs text-muted-foreground bg-muted rounded-md">
      System: {systemTheme === 'dark' ? 'Dark' : 'Light'} mode
    </div>
  );
}

function MobileThemeSwitcher({ sheetOpen, setSheetOpen, sheetTriggerRef, themeName, handleThemeChange, mode, handleModeChange, systemTheme }: {
  sheetOpen: boolean; setSheetOpen: (v: boolean) => void; sheetTriggerRef: React.RefObject<HTMLButtonElement | null>;
  themeName: ThemeName; handleThemeChange: (t: ThemeName) => void;
  mode: string; handleModeChange: (m: string) => void; systemTheme: string | undefined;
}) {
  return (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetTrigger asChild>
        <button
          ref={sheetTriggerRef}
          className="flex w-full items-center justify-center rounded border-2 border-foreground bg-transparent px-4 py-3 font-mono text-sm uppercase tracking-wider text-foreground transition-all hover:bg-foreground/10 min-h-[48px]"
          title="Change theme"
          aria-label="Change theme"
        >
          <Palette className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Theme Settings</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div>
            <h3 className="text-sm font-semibold mb-3">Color Palette</h3>
            <div className="space-y-2">
              {themes.map((theme) => (
                <button
                  key={theme.name}
                  onClick={() => handleThemeChange(theme.name)}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-colors min-h-[56px] ${
                    themeName === theme.name
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-transparent'
                  }`}
                >
                  <div className="text-left">
                    <div className="font-medium">{theme.label}</div>
                  </div>
                  {themeName === theme.name && <Check className="h-5 w-5 text-primary flex-shrink-0" />}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-3">Appearance</h3>
            <div className="space-y-2">
              {modes.map((modeOption) => (
                <button
                  key={modeOption.value}
                  onClick={() => handleModeChange(modeOption.value)}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-colors min-h-[56px] ${
                    mode === modeOption.value
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-transparent'
                  }`}
                >
                  <div className="flex items-center text-left">
                    {modeOption.icon}
                    <div>
                      <div className="font-medium">{modeOption.label}</div>
                      <div className="text-xs text-muted-foreground">{modeOption.description}</div>
                    </div>
                  </div>
                  {mode === modeOption.value && <Check className="h-5 w-5 text-primary flex-shrink-0" />}
                </button>
              ))}
            </div>
            <SystemModeLabel mode={mode} systemTheme={systemTheme} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DesktopThemeSwitcher({ themeName, setThemeName, mode, setMode, systemTheme }: {
  themeName: ThemeName; setThemeName: (t: ThemeName) => void;
  mode: string; setMode: (m: string) => void; systemTheme: string | undefined;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative theme-switcher-button"
          title="Change theme"
          aria-label="Change theme"
        >
          <Palette className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Mode</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={mode} onValueChange={setMode}>
          {modes.map((modeOption) => (
            <DropdownMenuRadioItem key={modeOption.value} value={modeOption.value} className="cursor-pointer">
              <div className="flex items-center">
                {modeOption.icon}
                <div>
                  <div className="font-medium">{modeOption.label}</div>
                  <div className="text-xs text-muted-foreground">{modeOption.description}</div>
                </div>
              </div>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>

        {mode === 'system' && systemTheme && (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            System: {systemTheme === 'dark' ? 'Dark' : 'Light'} mode
          </div>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        {themes.map((theme) => (
          <DropdownMenuItem key={theme.name} onClick={() => setThemeName(theme.name)} className="cursor-pointer">
            <div className="flex items-center justify-between w-full">
              <div className="font-medium">{theme.label}</div>
              {themeName === theme.name && <Check className="h-4 w-4 text-primary" />}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ThemeSwitcher() {
  const { themeName, setThemeName, mode, setMode, systemTheme } = useCustomTheme();
  const [isMobile, setIsMobile] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const sheetTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleThemeChange = useCallback(
    createDelayedSetter(setThemeName, setSheetOpen, isMobile),
    [setThemeName, isMobile]
  );

  const handleModeChange = useCallback(
    createDelayedSetter(setMode, setSheetOpen, isMobile),
    [setMode, isMobile]
  );

  if (isMobile) {
    return (
      <MobileThemeSwitcher
        sheetOpen={sheetOpen}
        setSheetOpen={setSheetOpen}
        sheetTriggerRef={sheetTriggerRef}
        themeName={themeName}
        handleThemeChange={handleThemeChange}
        mode={mode}
        handleModeChange={handleModeChange}
        systemTheme={systemTheme}
      />
    );
  }

  return (
    <DesktopThemeSwitcher
      themeName={themeName}
      setThemeName={setThemeName}
      mode={mode}
      setMode={setMode}
      systemTheme={systemTheme}
    />
  );
}
